/**
 * Reusable Modal Component
 */

import { CloseIcon, ModalBackgroundIcon } from "@/assets/svg";
import { useEffect } from "react";

export interface ModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}

export const Modal = ({
  isOpen,
  onClose,
  children,
}: ModalProps): React.JSX.Element | null => {
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className="relative z-10 bg-white rounded-2xl shadow-xl w-auto max-w-[90vw] p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-transparent text-gray-400 hover:text-gray-600 transition-colors z-20"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        {/* Background Icon */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden rounded-b-2xl -z-10">
          <ModalBackgroundIcon />
        </div>

        {children}
      </div>
    </div>
  );
};
